import { normalizedUuidSchema } from '@repo/api-contracts';
import { ArrowLeft } from 'lucide-react';
import type { ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router';
import { OrderStatusList, useOrderDetailQuery } from '@/entities/order';
import { useCompleteBankRefund, useConfirmDeposit } from '@/features/order/bank-transfer';
import {
  useRefundCancel,
  useRejectCancel,
  useSupplyFailureCancel,
} from '@/features/order/cancellation';
import { useNotifyDelay, useStartProduction } from '@/features/order/production';
import {
  useAddShipments,
  useMarkDelivered,
  useRemoveShipment,
  useRetryTracking,
  useShipOrder,
  useUpdateOrderAddress,
} from '@/features/order/shipping';
import { type OrderActions, orderErrorCode } from '@/shared/lib';
import { Button, EmptyState, PageHeader } from '@/shared/ui';
import { OrderDetail } from '@/widgets/order-detail';
import { OrderDetailInquiries } from '@/widgets/order-detail-inquiries';
import { OrderTaxInvoiceSection } from '@/widgets/order-tax-invoice';
import { ProductionOrderSheetSection } from '@/widgets/production-order-sheet';

/**
 * 어드민 주문 상세(주문 설계 §8-6) — 주소의 주문 id 로 상세를 읽고, 진행 처리 쓰기 훅(제작·지연·출고·송장·배송 완료·배송지·
 * 환불·반려·공급 불가)을 위젯 처리 버튼에 잇는다. 머리말은 주문 번호·주문 상태 배지·목록으로 버튼(받기 전에는 "주문 상세")이고,
 * 불러오는 중·못 찾음·실패는 카드 안 가운데 안내로 보인다. 견적 번호·"견적 상세에서 휴대폰 보기"는 그 주문의 견적 상세로 간다.
 */
export function OrderDetailPage(): ReactElement {
  const navigate = useNavigate();
  const { orderId } = useParams<{ orderId: string }>();
  const parsed = normalizedUuidSchema.safeParse(orderId);
  const id = parsed.success ? parsed.data : '';
  const query = useOrderDetailQuery(id, { enabled: parsed.success });
  const order = query.data;
  const paymentId = order?.payment?.id ?? null;
  const actions: OrderActions = {
    confirmDeposit: useConfirmDeposit(),
    completeBankRefund: useCompleteBankRefund(),
    startProduction: useStartProduction(),
    notifyDelay: useNotifyDelay(),
    ship: useShipOrder(),
    addShipments: useAddShipments(),
    removeShipment: useRemoveShipment(id),
    retryTracking: useRetryTracking(id),
    markDelivered: useMarkDelivered(),
    updateShippingAddress: useUpdateOrderAddress(),
    refundCancel: useRefundCancel(paymentId),
    rejectCancelRequest: useRejectCancel(id),
    cancelForSupplyFailure: useSupplyFailureCancel(paymentId),
  };

  let body: ReactElement;
  if (!parsed.success) {
    body = <EmptyState isError>올바른 주문 주소가 아닙니다.</EmptyState>;
  } else if (query.isPending) {
    body = <EmptyState>불러오는 중…</EmptyState>;
  } else if (!order && orderErrorCode(query.error) === 'ORDER_NOT_FOUND') {
    body = <EmptyState isError>주문을 찾을 수 없습니다.</EmptyState>;
  } else if (!order) {
    // 알림(alert)은 오류 글만 읽게 하고, 다시 시도 버튼은 안내 상자 아래 형제로 둔다.
    body = (
      <div className="flex flex-col gap-4">
        <EmptyState isError>주문을 불러오지 못했습니다: {query.error?.message}</EmptyState>
        <div className="flex justify-center">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              void query.refetch();
            }}
          >
            다시 시도
          </Button>
        </div>
      </div>
    );
  } else {
    body = (
      <>
        <OrderDetail
          key={order.id}
          order={order}
          actions={actions}
          onReload={() => {
            void query.refetch();
          }}
          onQuoteOpen={(quoteRequestId) => {
            void navigate(`/quotes/${quoteRequestId}`);
          }}
          pending={query.isFetching}
          taxInvoice={<OrderTaxInvoiceSection orderId={order.id} order={order} />}
          productionOrderSheet={<ProductionOrderSheetSection orderId={order.id} order={order} />}
          error={
            query.isError
              ? '최신 내용을 불러오지 못했습니다. 지금 화면은 마지막으로 받은 내용입니다.'
              : null
          }
        />
        <OrderDetailInquiries orderId={order.id} />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={order ? `주문 ${order.orderNumber}` : '주문 상세'}
        description={order ? <OrderStatusList order={order} /> : undefined}
        actions={
          <Button
            type="button"
            variant="outline"
            className="h-10 px-5"
            onClick={() => {
              void navigate('/orders');
            }}
          >
            <ArrowLeft aria-hidden="true" />
            목록으로
          </Button>
        }
      />
      {body}
    </>
  );
}
